import "./App.css";
import { useState } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import HomePage from "./pages/HomePage";
import StaffPortal from "./pages/StaffPortal";
import SurgeonsDatabase from "./pages/SurgeonsDatabase";
import UKPopulationExplorer from "./pages/UKPopulationExplorer";
import Purchasing from "./pages/Purchasing";
import EyeExamChart from "./pages/EyeExamChart";
import ZammadAttachments from "./pages/ZammadAttachments";
import FreeScoutAttachments from "./pages/FreeScoutAttachments";
import UserProfile from "./pages/UserProfile";
import AccountSettings from "./pages/AccountSettings";

/**
 * Root component and view switcher for the VetFlow SPA.
 *
 * IMPORTANT: this app does NOT use React Router. "Navigation" is a
 * string in React state (currentView) swapped by a switch statement —
 * the URL never changes. Consequences:
 *   - No deep-linking: you cannot bookmark or share a link to a page;
 *     every visit starts at 'home'/'Loading...'.
 *   - Browser Back/Forward buttons do nothing (no history entries are
 *     pushed), and refresh always returns to the default view.
 *   - The only URLs that exist are '/' itself and the OAuth callback
 *     parameters, which AuthContext consumes on mount then strips.
 *
 * Authorization model: VetFlow is staff-only (the client portal was
 * retired with the insurance claims domain; see the
 * archive/insurance-claims branch). ProtectedRoute only gates the React
 * tree — it is UI-level convenience, not security. Real enforcement lives
 * in the FastAPI dependencies on each endpoint. user.isStaff is derived
 * from the JWT's user_type claim via /api/auth/me (see AuthContext), and
 * an authenticated token that is not staff is shown the login screen.
 * Views behind a second permission wall additionally pass an `app` prop
 * so ProtectedRoute can consult the role-permission grid (see
 * AuthContext.can) before mounting them.
 */

// Shown when an authenticated staff user opens a view their role's
// permission grid does not cover (can(app, 'view') === false). This is
// UI-level convenience — the API returns 403 for the same user — but
// catching it here avoids mounting a page that can only render errors.
const NoAccess = ({ navigateTo }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
    <div className="max-w-md text-center">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">No access</h2>
      <p className="text-sm text-gray-600 mb-6">
        Access to this application is granted by role. If you believe you
        should have it, please contact an administrator.
      </p>
      <button
        onClick={() => navigateTo('home')}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
      >
        Back to portal
      </button>
    </div>
  </div>
);

// Protected Route Component — mounts children only when authenticated
// and staff. Falls back to <HomePage/> rather than redirecting, since
// there are no routes to redirect to. The system is staff-only, so any
// authenticated token that is not staff (e.g. a historical client token)
// gets the login screen too — there is no client view to fall back to.
// There is no `requireStaff` prop any more: every view behind this guard
// requires staff, so the check is unconditional rather than opt-in.
// The optional `app` prop adds a second gate: the user must hold the
// 'view' capability for that app in the grid fetched by AuthContext.
// While permissions are still loading we show the same Loading state
// rather than flash-permit then snatch the page away.
const ProtectedRoute = ({ children, app, navigateTo }) => {
  const { user, isAuthenticated, loading, can, permissionsLoading } = useAuth();

  if (loading || (app && permissionsLoading)) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <HomePage />;
  }

  if (!user?.isStaff) {
    return <HomePage />;
  }

  if (app && !can(app, 'view')) {
    return <NoAccess navigateTo={navigateTo} />;
  }

  return children;
};

// Main App Content Component
const AppContent = () => {
  const [currentView, setCurrentView] = useState('home');
  const { user, isAuthenticated, loading } = useAuth();

  // Passed down to every page as the navigation API — the only way to
  // change "pages" is to call navigateTo('view-name'); the names must
  // match the switch cases below.
  const navigateTo = (view) => {
    setCurrentView(view);
    // No URL changes - pure state management
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <HomePage navigateTo={navigateTo} />;
  }

  // Render based on current view
  switch (currentView) {
    // 'home' isn't a page of its own — the only portal is the staff one.
    // Still guarded: the check above only proves the user is authenticated,
    // not that they are staff.
    case 'home':
      return (
        <ProtectedRoute>
          <StaffPortal navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'staff-portal':
      return (
        <ProtectedRoute>
          <StaffPortal navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'surgeons-database':
      return (
        <ProtectedRoute app="surgeons" navigateTo={navigateTo}>
          <SurgeonsDatabase navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'purchasing':
      return (
        <ProtectedRoute app="purchasing" navigateTo={navigateTo}>
          <Purchasing navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'uk-population':
      return (
        <ProtectedRoute app="population" navigateTo={navigateTo}>
          <UKPopulationExplorer navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'eye-exam':
      return (
        <ProtectedRoute app="eye-exam" navigateTo={navigateTo}>
          <EyeExamChart navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'zammad-attachments':
      return (
        <ProtectedRoute app="zammad-attachments" navigateTo={navigateTo}>
          <ZammadAttachments navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'freescout-attachments':
      return (
        // Shares the 'zammad-attachments' permission app — one matrix
        // row governs both ticket systems by product decision.
        <ProtectedRoute app="zammad-attachments" navigateTo={navigateTo}>
          <FreeScoutAttachments navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'profile':
      return (
        <ProtectedRoute>
          <UserProfile navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    case 'settings':
      return (
        <ProtectedRoute>
          <AccountSettings navigateTo={navigateTo} />
        </ProtectedRoute>
      );
    default:
      return <HomePage navigateTo={navigateTo} />;
  }
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
